import { useMemo } from 'react';

import { TaxonomyType } from '@revelio/data-access';
import type { TreeData } from '@revelio/ui';

import { ACTIVITY_TAXONOMY_TREE_LEVELS } from '../../../next/CustomDash/filters/utils/taxonomy-tree-levels';
import { toNestedTaxonomyTree } from '../../../next/CustomDash/filters/utils/taxonomyNestedTree';
import { useActivityTaxonomy } from '../../user-preferences/hooks/useActivityTaxonomySelection';
import { ACTIVITY_TAXONOMY_SELECTION_LIST_KEYS } from '../types';
import type { TaxonomyLevelMetadata } from '../utils/taxonomyTree';
import { useCurrentReferenceTaxonomyVersion } from './useReferenceTaxonomyVersions';
import { useTaxonomyTree } from './useTaxonomyTree';

type ActivityTreeNode = ReturnType<typeof toNestedTaxonomyTree>[number];

type UseActivityTaxonomyTreeResult = {
  activityTree: ActivityTreeNode[] | null;
  treeData: TreeData[];
  levels: TaxonomyLevelMetadata[];
  isActivityTaxonomyPending: boolean;
};

export const useActivityTaxonomyTree = ({
  overrideTaxonomyId,
  taxonomyVersionOverride,
  enabled = true,
  levels: requestedLevels,
}: {
  overrideTaxonomyId?: number;
  taxonomyVersionOverride?: string;
  enabled?: boolean;
  levels?: number[];
} = {}): UseActivityTaxonomyTreeResult => {
  const prefTaxonomyId = useActivityTaxonomy();
  const activityTaxonomyId = overrideTaxonomyId ?? prefTaxonomyId ?? 0;

  const { version: defaultActivityTaxonomyVersion, loading: isVersionLoading } =
    useCurrentReferenceTaxonomyVersion({
      taxonomyType: TaxonomyType.Activity,
      pause: !enabled,
    });
  const taxonomyVersion =
    taxonomyVersionOverride ?? defaultActivityTaxonomyVersion;

  const {
    treeData,
    levels,
    fetching: isTreeFetching,
  } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Activity,
    taxonomyId: activityTaxonomyId,
    taxonomyVersion,
    levels: requestedLevels ?? ACTIVITY_TAXONOMY_TREE_LEVELS,
    // This request will never be made until the backend returns a version for activity
    pause: !enabled || isVersionLoading || !taxonomyVersion,
  });

  const activityTree = useMemo(() => {
    if (!enabled || requestedLevels !== undefined || treeData.length === 0) {
      return null;
    }
    const nested = toNestedTaxonomyTree({
      nodes: treeData,
      selectionListIds: ACTIVITY_TAXONOMY_SELECTION_LIST_KEYS,
      includeNodeMetadata: true,
      filterInvalidNodeNames: false,
    });
    return nested.length > 0 ? nested : null;
  }, [enabled, requestedLevels, treeData]);

  return {
    activityTree,
    treeData,
    levels,
    isActivityTaxonomyPending: enabled && (isVersionLoading || isTreeFetching),
  };
};
